import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  GraduationCap,
  CheckCircle,
  Sparkles,
  Tag,
  Receipt,
  Users,
  ChevronRight,
  ArrowLeft,
  Copy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReenrollmentCard() {
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('overview'); // overview | configure | confirm | success
  const [paymentPlan, setPaymentPlan] = useState('full');
  const [depositsPaid, setDepositsPaid] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/reenrollment/my-status`, { withCredentials: true });
      setStatus(res.data);
      // Pre-check deposits that are already paid
      if (res.data.deposits_paid?.length > 0) {
        const preChecked = {};
        res.data.deposits_paid.forEach(name => { preChecked[name] = true; });
        setDepositsPaid(preChecked);
      }
    } catch (err) {
      console.error('Error fetching reenrollment status:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (!status || status.year_2526.count === 0) return null;

  const pricing = status.pricing;
  const children2526 = status.year_2526.children;
  const children2627 = status.year_2627.children;
  // Only count as "submitted" the children the parent actually re-enrolled.
  // The admin-created 'pendiente' placeholders are NOT a submitted enrollment.
  const submitted2627 = children2627.filter(c => c.status && c.status !== 'pendiente');
  const alreadyEnrolled2627 = submitted2627.length > 0;

  const calculatePrice = () => {
    const basePrice = paymentPlan === 'full' ? pricing.annual_price : pricing.trimester_price;
    const numChildren = children2526.length;
    let total = 0;
    const breakdown = [];

    for (let i = 0; i < numChildren; i++) {
      let childPrice = basePrice;
      let discounts = [];
      const childName = children2526[i].name;

      // Sibling discount on 2nd+ child
      if (i >= 1) {
        const siblingDiscount = Math.round(basePrice * pricing.sibling_discount_pct / 100);
        childPrice -= siblingDiscount;
        discounts.push({ label: language === 'es' ? 'Descuento hermano (-20%)' : 'Sibling discount (-20%)', amount: siblingDiscount });
      }

      // Returning family discount on 1st child only
      if (i === 0) {
        childPrice -= pricing.returning_discount;
        discounts.push({ label: language === 'es' ? 'Descuento familia que regresa' : 'Returning family discount', amount: pricing.returning_discount });
      }

      // Deposit per child
      if (depositsPaid[childName]) {
        childPrice -= pricing.deposit_amount;
        discounts.push({ label: language === 'es' ? 'Deposito ya pagado' : 'Deposit already paid', amount: pricing.deposit_amount });
      }

      childPrice = Math.max(0, childPrice);
      total += childPrice;
      breakdown.push({
        name: childName,
        level: children2526[i].level,
        base: basePrice,
        discounts,
        final: childPrice,
      });
    }

    return { total, breakdown };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const childrenData = children2526.map(c => ({ name: c.name, level: c.level, deposit_paid: !!depositsPaid[c.name] }));
      const res = await axios.post(`${API}/reenrollment/submit`, {
        children: childrenData,
        payment_plan: paymentPlan,
        apply_returning_discount: true,
        deposits_per_child: depositsPaid,
      }, { withCredentials: true });
      setResult(res.data);
      setStep('success');
      toast.success(language === 'es' ? 'Reinscripcion enviada' : 'Re-enrollment submitted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const { total, breakdown } = calculatePrice();

  // Already enrolled for 26/27
  if (alreadyEnrolled2627) {
    return (
      <div className="card-apple p-8 border-l-4 border-[#34C759]" data-testid="reenrollment-done">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#34C759]/10 flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-[#34C759]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#1D1D1F]">
              {language === 'es' ? 'Inscripcion 2026-2027' : 'Enrollment 2026-2027'}
            </h3>
            <p className="text-sm text-[#34C759] font-medium">
              {language === 'es' ? 'Inscripcion enviada' : 'Enrollment submitted'}
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {submitted2627.map((c, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl text-sm">
              <span className="font-medium text-[#1D1D1F]">{c.name}</span>
              <span className="text-[#86868B]">${c.amount?.toLocaleString()} — {c.status}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Success step
  if (step === 'success' && result) {
    return (
      <div className="card-apple p-8 border-l-4 border-[#34C759]" data-testid="reenrollment-success">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-[#34C759]/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-[#34C759]" />
          </div>
          <h3 className="text-xl font-semibold text-[#1D1D1F]">
            {language === 'es' ? 'Reinscripcion Confirmada' : 'Re-enrollment Confirmed'}
          </h3>
          <p className="text-sm text-[#86868B] mt-1">
            {language === 'es' ? 'Ano Escolar 2026-2027' : 'School Year 2026-2027'}
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {result.enrollments.map((e, i) => (
            <div key={i} className="p-4 bg-[#F5F5F7] rounded-xl">
              <div className="flex justify-between items-center">
                <span className="font-medium text-[#1D1D1F]">{e.student_name}</span>
                <span className="font-bold text-[#1D1D1F]">${e.final_price?.toLocaleString()}</span>
              </div>
            </div>
          ))}
          <div className="p-4 bg-[#1D1D1F] rounded-xl text-white flex justify-between items-center">
            <span className="font-medium">{language === 'es' ? 'Total a pagar' : 'Total due'}</span>
            <span className="text-xl font-bold">${result.total_amount?.toLocaleString()}</span>
          </div>
        </div>

        {/* Zelle Instructions */}
        <div className="p-5 bg-[#7B61FF]/5 border border-[#7B61FF]/20 rounded-xl">
          <h4 className="font-semibold text-[#1D1D1F] mb-3 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-[#7B61FF]" />
            {language === 'es' ? 'Instrucciones de Pago — Zelle' : 'Payment Instructions — Zelle'}
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#86868B]">{language === 'es' ? 'Destinatario' : 'Recipient'}</span>
              <button 
                className="font-medium text-[#1D1D1F] flex items-center gap-1 hover:text-[#7B61FF]"
                onClick={() => { navigator.clipboard.writeText('ARCE SPANISH SCHOOL, LLC.'); toast.success('Copiado'); }}
                data-testid="copy-zelle-recipient"
              >
                ARCE SPANISH SCHOOL, LLC. <Copy className="h-3 w-3" />
              </button>
            </div>
            <div className="flex justify-between">
              <span className="text-[#86868B]">{language === 'es' ? 'Monto' : 'Amount'}</span>
              <span className="font-bold text-[#1D1D1F]">${result.total_amount?.toLocaleString()}</span>
            </div>
          </div>
          <p className="text-xs text-[#86868B] mt-3">
            {language === 'es' 
              ? 'Envie su pago por Zelle y recibiremos una notificacion automatica. Su inscripcion sera confirmada una vez verificado el pago.'
              : 'Send your payment via Zelle and we will receive an automatic notification. Your enrollment will be confirmed once payment is verified.'}
          </p>
        </div>
      </div>
    );
  }

  // Configure step — price calculator
  if (step === 'configure') {
    return (
      <div className="card-apple p-8 border-l-4 border-[#E63946]" data-testid="reenrollment-configure">
        <button onClick={() => setStep('overview')} className="flex items-center gap-1 text-sm text-[#86868B] hover:text-[#1D1D1F] mb-4">
          <ArrowLeft className="h-4 w-4" /> {language === 'es' ? 'Volver' : 'Back'}
        </button>

        <h3 className="text-xl font-semibold text-[#1D1D1F] mb-1">
          {language === 'es' ? 'Reinscripcion 2026-2027' : 'Re-enrollment 2026-2027'}
        </h3>
        <p className="text-sm text-[#86868B] mb-6">
          {language === 'es' ? 'Configure su plan de pago' : 'Configure your payment plan'}
        </p>

        {/* Payment Plan Selection */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => setPaymentPlan('full')}
            className={`p-4 rounded-xl border-2 text-left transition-all ${paymentPlan === 'full' ? 'border-[#E63946] bg-[#E63946]/5' : 'border-black/10 hover:border-black/20'}`}
            data-testid="plan-full"
          >
            <p className="font-semibold text-[#1D1D1F]">{language === 'es' ? 'Pago Anual' : 'Annual'}</p>
            <p className="text-2xl font-bold text-[#E63946]">${pricing.annual_price.toLocaleString()}</p>
            <p className="text-xs text-[#86868B]">{language === 'es' ? 'por alumno / ano' : 'per student / year'}</p>
          </button>
          <button
            onClick={() => setPaymentPlan('trimester')}
            className={`p-4 rounded-xl border-2 text-left transition-all ${paymentPlan === 'trimester' ? 'border-[#E63946] bg-[#E63946]/5' : 'border-black/10 hover:border-black/20'}`}
            data-testid="plan-trimester"
          >
            <p className="font-semibold text-[#1D1D1F]">{language === 'es' ? 'Trimestral' : 'Trimester'}</p>
            <p className="text-2xl font-bold text-[#E63946]">${pricing.trimester_price.toLocaleString()}</p>
            <p className="text-xs text-[#86868B]">{language === 'es' ? 'por alumno / trimestre' : 'per student / trimester'}</p>
          </button>
        </div>

        {/* Deposit checkboxes per child */}
        <div className="space-y-2 mb-6">
          <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide">
            {language === 'es' ? 'Deposito de $100 ya pagado' : '$100 deposit already paid'}
          </p>
          {children2526.map((child, i) => {
            const isPrepaid = status?.deposits_paid?.includes(child.name);
            return (
            <label key={i} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer ${isPrepaid ? 'bg-[#34C759]/5 border border-[#34C759]/20' : 'bg-[#F5F5F7]'}`} data-testid={`deposit-checkbox-${i}`}>
              <input
                type="checkbox"
                checked={!!depositsPaid[child.name]}
                onChange={(e) => setDepositsPaid(prev => ({ ...prev, [child.name]: e.target.checked }))}
                className="w-5 h-5 rounded border-black/20 text-[#E63946] focus:ring-[#E63946]"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1D1D1F]">
                  {child.name} — {language === 'es' ? 'deposito pagado (-$100)' : 'deposit paid (-$100)'}
                </p>
                {isPrepaid && (
                  <p className="text-xs text-[#34C759] font-medium">{language === 'es' ? 'Deposito registrado' : 'Deposit on file'}</p>
                )}
              </div>
            </label>
            );
          })}
        </div>

        {/* Price Breakdown */}
        <div className="border border-black/10 rounded-xl overflow-hidden mb-6">
          <div className="px-4 py-3 bg-[#F5F5F7]">
            <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide">
              {language === 'es' ? 'Detalle del precio' : 'Price breakdown'}
            </p>
          </div>
          {breakdown.map((child, i) => (
            <div key={i} className="p-4 border-b border-black/5 last:border-b-0">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-[#E63946]" />
                  <span className="font-medium text-[#1D1D1F]">{child.name}</span>
                  <span className="text-xs px-2 py-0.5 bg-[#E63946]/10 text-[#E63946] rounded-full">{child.level}</span>
                </div>
              </div>
              <div className="space-y-1 text-sm ml-6">
                <div className="flex justify-between text-[#86868B]">
                  <span>{language === 'es' ? 'Precio base' : 'Base price'}</span>
                  <span>${child.base.toLocaleString()}</span>
                </div>
                {child.discounts.map((d, j) => (
                  <div key={j} className="flex justify-between text-[#34C759]">
                    <span className="flex items-center gap-1"><Tag className="h-3 w-3" /> {d.label}</span>
                    <span>-${d.amount.toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between font-semibold text-[#1D1D1F] pt-1 border-t border-black/5">
                  <span>{language === 'es' ? 'Subtotal' : 'Subtotal'}</span>
                  <span>${child.final.toLocaleString()}</span>
                </div>
              </div>
            </div>
          ))}
          <div className="px-4 py-4 bg-[#1D1D1F] text-white flex justify-between items-center">
            <span className="font-semibold">{language === 'es' ? 'Total a pagar' : 'Total due'}</span>
            <span className="text-2xl font-bold">${total.toLocaleString()}</span>
          </div>
        </div>

        <Button
          onClick={() => setStep('confirm')}
          className="w-full py-6 rounded-xl bg-[#E63946] hover:bg-[#D62828] text-white font-medium text-base"
          data-testid="reenrollment-review-btn"
        >
          {language === 'es' ? 'Revisar y confirmar' : 'Review and confirm'}
          <ChevronRight className="h-5 w-5 ml-2" />
        </Button>
      </div>
    );
  }

  // Confirm step
  if (step === 'confirm') {
    return (
      <div className="card-apple p-8 border-l-4 border-[#FF9500]" data-testid="reenrollment-confirm">
        <button onClick={() => setStep('configure')} className="flex items-center gap-1 text-sm text-[#86868B] hover:text-[#1D1D1F] mb-4">
          <ArrowLeft className="h-4 w-4" /> {language === 'es' ? 'Modificar' : 'Modify'}
        </button>

        <h3 className="text-xl font-semibold text-[#1D1D1F] mb-4">
          {language === 'es' ? 'Confirmar Reinscripcion' : 'Confirm Re-enrollment'}
        </h3>

        <div className="space-y-3 mb-6">
          {breakdown.map((child, i) => (
            <div key={i} className="p-4 bg-[#F5F5F7] rounded-xl flex justify-between items-center">
              <div>
                <p className="font-medium text-[#1D1D1F]">{child.name}</p>
                <p className="text-xs text-[#86868B]">{child.level} — {paymentPlan === 'full' ? (language === 'es' ? 'Anual' : 'Annual') : (language === 'es' ? 'Trimestral' : 'Trimester')}</p>
              </div>
              <span className="font-bold text-[#1D1D1F]">${child.final.toLocaleString()}</span>
            </div>
          ))}

          {/* Discounts applied summary */}
          <div className="p-4 bg-[#34C759]/5 border border-[#34C759]/20 rounded-xl">
            <p className="text-sm font-medium text-[#34C759] flex items-center gap-1 mb-2">
              <Sparkles className="h-4 w-4" />
              {language === 'es' ? 'Descuentos aplicados' : 'Discounts applied'}
            </p>
            <div className="text-xs text-[#34C759] space-y-1">
              <p>- {language === 'es' ? 'Familia que regresa' : 'Returning family'}: -${pricing.returning_discount}</p>
              {Object.entries(depositsPaid).filter(([,v]) => v).map(([name], i) => (
                <p key={i}>- {language === 'es' ? 'Deposito' : 'Deposit'} {name}: -${pricing.deposit_amount}</p>
              ))}
              {children2526.length > 1 && <p>- {language === 'es' ? 'Descuento hermano(s)' : 'Sibling discount'}: -20%</p>}
            </div>
          </div>

          <div className="p-4 bg-[#1D1D1F] rounded-xl text-white flex justify-between items-center">
            <span className="font-medium">{language === 'es' ? 'Total' : 'Total'}</span>
            <span className="text-2xl font-bold">${total.toLocaleString()}</span>
          </div>

          <p className="text-xs text-[#86868B] text-center">
            {language === 'es'
              ? 'Metodo de pago: Zelle — ARCE SPANISH SCHOOL, LLC.'
              : 'Payment method: Zelle — ARCE SPANISH SCHOOL, LLC.'}
          </p>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-6 rounded-xl bg-[#E63946] hover:bg-[#D62828] text-white font-medium text-base"
          data-testid="reenrollment-confirm-btn"
        >
          {submitting
            ? (language === 'es' ? 'Procesando...' : 'Processing...')
            : (language === 'es' ? 'Confirmar Reinscripcion' : 'Confirm Re-enrollment')}
        </Button>
      </div>
    );
  }

  // Overview step — invitation card
  return (
    <div className="card-apple overflow-hidden" data-testid="reenrollment-card">
      {/* Header banner */}
      <div className="bg-gradient-to-r from-[#1D1D1F] to-[#3A3A3C] p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-white/60 uppercase tracking-wider mb-1">
              {language === 'es' ? 'Nuevo Ano Escolar' : 'New School Year'}
            </p>
            <h3 className="text-2xl font-bold">2026-2027</h3>
          </div>
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-[#FFD60A]" />
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Children from 25/26 */}
        <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide mb-3">
          {language === 'es' ? 'Reinscribir a sus hijos' : 'Re-enroll your children'}
        </p>
        <div className="space-y-2 mb-5">
          {children2526.map((child, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-[#F5F5F7] rounded-xl">
              <div className="w-9 h-9 rounded-full bg-[#E63946]/10 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="h-4 w-4 text-[#E63946]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1D1D1F] truncate">{child.name}</p>
                <p className="text-xs text-[#86868B]">{language === 'es' ? 'Nivel' : 'Level'}: {child.level}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Discount badges */}
        <div className="flex flex-wrap gap-2 mb-5">
          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#34C759]/10 text-[#34C759] text-xs font-medium rounded-full">
            <Tag className="h-3 w-3" /> -${pricing.returning_discount} {language === 'es' ? 'familia que regresa' : 'returning family'}
          </span>
          {children2526.length > 1 && (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#007AFF]/10 text-[#007AFF] text-xs font-medium rounded-full">
              <Users className="h-3 w-3" /> -20% {language === 'es' ? 'hermano' : 'sibling'}
            </span>
          )}
        </div>

        {/* Pricing preview */}
        <div className="flex items-baseline gap-2 mb-5">
          <span className="text-xs text-[#86868B]">{language === 'es' ? 'Desde' : 'From'}</span>
          <span className="text-3xl font-bold text-[#1D1D1F]">
            ${(pricing.annual_price - pricing.returning_discount).toLocaleString()}
          </span>
          <span className="text-sm text-[#86868B]">/ {language === 'es' ? 'ano' : 'year'}</span>
        </div>

        <Button
          onClick={() => setStep('configure')}
          className="w-full py-5 rounded-xl bg-[#E63946] hover:bg-[#D62828] text-white font-medium"
          data-testid="reenrollment-start-btn"
        >
          {language === 'es' ? 'Inscribir para 2026-2027' : 'Enroll for 2026-2027'}
          <ChevronRight className="h-5 w-5 ml-2" />
        </Button>
      </div>
    </div>
  );
}
